/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { forwardRef, KeyboardEvent } from "react";

import Grid, { GridProps } from "@mui/material/Grid";
import classnames from "classnames";

import { getTransitions } from "../css";
import { DashboardButton } from "../DashboardButton/DashboardButton";
import { DashboardCheckbox } from "../DashboardCheckbox";
import { DashboardSlotSelect } from "../DashboardSlotSelect";
import { DashboardIcon } from "../Icon/DashboardIcon";
import { DashboardTypography } from "../Typography/DashboardTypography";

import { zeffySelectBarClasses, SelectBarProps } from "./types";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
import { ChevronRight } from "@/icons/outlined";

const selectBarSx: GridProps["sx"] = (theme) => ({
  [`&.${zeffySelectBarClasses.container}`]: {
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.border.neutral.quiet}`,
    borderRadius: theme.spacing(1),
    ...getTransitions(),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1.5, 3),
    },
  },
  [`&.${zeffySelectBarClasses.clickable}`]: {
    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.palette.surface.neutral.supershy,
      [`& .${zeffySelectBarClasses.icon}`]: {
        fill: theme.palette.text.neutral.moderate,
      },
    },
  },
  [`&.${zeffySelectBarClasses.containerSelected}`]: {
    borderColor: theme.palette.surface.brand.intense,
  },
  [`& .${zeffySelectBarClasses.icon}`]: {
    fill: theme.palette.text.neutral.moderate,
  },
  "& .MuiGrid-item:first-child": {
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  [`&.${zeffySelectBarClasses.importVariant}`]: {
    padding: theme.spacing(1.5, 3, 1.5, 2),
    borderRadius: theme.spacing(1),
    borderColor: theme.palette.border.neutral.quiet,
    backgroundColor: theme.palette.surface.neutral.white,
    [`&.${zeffySelectBarClasses.containerSelected}`]: {
      borderColor: theme.palette.surface.brand.quiet,
      backgroundColor: theme.palette.surface.brand.quiet,
    },
    [`&.${zeffySelectBarClasses.containerSelected}.${zeffySelectBarClasses.clickable}:hover`]: {
      backgroundColor: theme.palette.surface.brand.moderate,
    },
  },
  [`&.${zeffySelectBarClasses.disabled}`]: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

export const DashboardSelectBar = forwardRef<HTMLDivElement, SelectBarProps>(
  (
    {
      title,
      description,
      onClick,
      icon,
      selected,
      iconProps,
      hideArrow,
      renderIcon,
      action,
      sx,
      displayDotSelect,
      illustration,
      variant = "default",
      disabled = false,
      "data-test": dataTest,
    },
    ref
  ) => {
    const isImport = variant === "import";
    const isClickable = !!onClick && !disabled;
    return (
      <Grid
        container
        className={classnames(zeffySelectBarClasses.container, {
          [zeffySelectBarClasses.containerSelected]: selected,
          [zeffySelectBarClasses.clickable]: isClickable,
          [zeffySelectBarClasses.importVariant]: isImport,
          [zeffySelectBarClasses.disabled]: disabled,
        })}
        onClick={isClickable ? onClick : undefined}
        sx={[selectBarSx, ...(Array.isArray(sx) ? sx : [sx])]}
        data-test={dataTest}
        ref={ref}
        {...(isImport && {
          role: "checkbox",
          "aria-checked": Boolean(selected),
          "aria-disabled": disabled,
          tabIndex: disabled ? -1 : 0,
          onKeyDown: (event: KeyboardEvent) => {
            if (isClickable && (event.key === " " || event.key === "Enter")) {
              event.preventDefault();
              onClick?.();
            }
          },
        })}
      >
        <Grid container justifyContent="space-between" alignItems="center">
          {typeof illustration === "string" ? (
            <Grid item>
              <Image src={illustration} alt="" width={64} height={64} />
            </Grid>
          ) : (
            illustration && <Grid item>{illustration}</Grid>
          )}
          <Grid item xs>
            <Grid container direction="column">
              {(renderIcon || (icon && !illustration)) && (
                <Grid item>
                  {renderIcon ||
                    (icon && (
                      <DashboardIcon size="large" {...(iconProps || {})}>
                        {icon}
                      </DashboardIcon>
                    ))}
                </Grid>
              )}
              <Grid item>
                {typeof title === "string" ? (
                  <DashboardTypography variant="subtitle1" vibe={isImport ? "text-neutral-intense" : undefined}>
                    {title}
                  </DashboardTypography>
                ) : (
                  title
                )}
              </Grid>
              <Grid item>
                {typeof description === "string" ? (
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {description}
                  </DashboardTypography>
                ) : (
                  description
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {action && (
              <DashboardButton
                variant="outlined"
                vibe="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                endIcon={action.icon}
              >
                {action.label}
              </DashboardButton>
            )}
            {!hideArrow && !isImport && (
              <DashboardIcon className={zeffySelectBarClasses.icon}>
                <ChevronRight />
              </DashboardIcon>
            )}
            {displayDotSelect && <DashboardSlotSelect isSelected={Boolean(selected)} size="small" />}
            {isImport && (
              <DashboardCheckbox
                checked={Boolean(selected)}
                disabled={disabled}
                tabIndex={-1}
                inputProps={{ "aria-hidden": true, tabIndex: -1 }}
                sx={{ pointerEvents: "none" }}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

DashboardSelectBar.displayName = "DashboardSelectBar";
